import {Button} from '@dropbox/dig-components/buttons';
import {
  DatePickerRange,
  type DatePickerRangeSelection,
  type DatePickerSelection,
} from '@dropbox/dig-components/date_picker';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  ArrowRightLine,
  ChevronDownLine,
  ChevronRightLine,
  DownloadLine,
} from '@dropbox/dig-icons/assets';
import {useQueries, useQuery} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import type {EmployeeRoleLevel, WorkstreamAuditLog as WorkstreamAuditLogType} from 'client';
import {Workstream, WorkstreamService} from 'client';
import {OpenAPI} from 'client/core/OpenAPI';
import {request as __request} from 'client/core/request';
import {parseAndExtractTextFromLexicalJson} from 'components/DSYS/editor/utils/lexical';
import {EmployeeButton} from 'components/DSYS/EmployeeButton';
import {Layout} from 'components/DSYS/Layout';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {UpdatePost} from 'components/DSYS/UpdatePost';
import {ChangeLogLine} from 'components/teams/TeamAuditLog';
import {ROUTE_PATHS} from 'constant';
import {endOfWeek, parseISO, startOfWeek, subWeeks} from 'date-fns';
import {isUserAdmin} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router';
import {getService} from 'utilities';
import {getFullWorkstreamMetrics, getWorkstreamDri} from 'views/workstreams/utils';
import {
  AuditLogReason,
  getMetricTitle,
  groupByTimeAndType,
  WorkstreamChangesByMetric,
} from 'views/workstreams/WorkstreamAuditLog';

const formatDateForApi = (d: Date) => d.toISOString().slice(0, 10);

const escapeCsvField = (value: string | null | undefined): string => {
  if (value == null) return '';
  const s = String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const METRIC_FIELD_LABELS: Record<string, string> = {
  title: 'title',
  type: 'type',
  current: 'current',
  target: 'target',
  owner_id: 'owner',
  complete_date: 'complete date',
  complete: 'complete',
};

function formatMetricFieldValue(key: string, value: unknown): string {
  if (value == null) return '';
  if (key === 'title') {
    try {
      const s = typeof value === 'string' ? value : JSON.stringify(value);
      return parseAndExtractTextFromLexicalJson(s) || s;
    } catch {
      return String(value);
    }
  }
  if (key === 'complete_date' && typeof value === 'string') {
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return value;
    }
  }
  return String(value);
}

function getMetricTitlePlainText(metricJsonValue: string): string {
  try {
    const metric = JSON.parse(metricJsonValue) as Record<string, unknown>;
    const title = metric.title;
    if (title == null) return '';
    const s = typeof title === 'string' ? title : JSON.stringify(title);
    return parseAndExtractTextFromLexicalJson(s) || String(title);
  } catch {
    return '';
  }
}

function getOutcomeIdFromLog(log: WorkstreamAuditLogType): string {
  const payload = log.after ?? log.before;
  if (!payload) return '';
  try {
    const obj = JSON.parse(payload) as Record<string, unknown>;
    const id = obj.id;
    return id != null ? String(id) : '';
  } catch {
    return '';
  }
}

/** Workstream milestone kind from metric JSON: metric | milestone */
function getOutcomeKindFromLog(log: WorkstreamAuditLogType): string {
  const payload = log.after ?? log.before;
  if (!payload) return '';
  try {
    const obj = JSON.parse(payload) as Record<string, unknown>;
    const kind = obj.type;
    if (kind === 'deliverable') {
      return 'milestone';
    }
    return typeof kind === 'string' ? kind : '';
  } catch {
    return '';
  }
}

/** UTC calendar date and time parts for CSV (API stores audit timestamps in UTC). */
function splitAuditLogDateTime(dateStr: string): {date: string; time: string} {
  if (!dateStr) return {date: '', time: ''};
  const hasTz = /Z$/i.test(dateStr) || /[+-]\d{2}:?\d{2}$/.test(dateStr);
  const normalized = hasTz ? dateStr : `${dateStr}Z`;
  const d = parseISO(normalized);
  if (Number.isNaN(d.getTime())) return {date: dateStr, time: ''};
  const iso = d.toISOString();
  return {date: iso.slice(0, 10), time: iso.slice(11, -1)};
}

function getMetricNameFromLog(log: WorkstreamAuditLogType): string {
  const payload = log.after ?? log.before;
  return payload ? getMetricTitlePlainText(payload) : '';
}

function expandLogToCsvRows(
  log: WorkstreamAuditLogType,
  ws: Workstream | undefined,
  dri: {name: string; ldap: string} | null
): string[][] {
  const workstreamUrl =
    log.workstream_id != null ? `https://os.dropbox.com/workstreams/${log.workstream_id}` : '';
  const profileUrl = dri?.ldap ? `https://os.dropbox.com/people/${dri.ldap}` : '';
  const outcomeId = getOutcomeIdFromLog(log);
  const metricName = escapeCsvField(getMetricNameFromLog(log));
  const {date: datePart, time: timePart} = splitAuditLogDateTime(log.date);
  const prefix = [
    escapeCsvField(workstreamUrl),
    escapeCsvField(ws?.name ?? ''),
    escapeCsvField(datePart),
    escapeCsvField(timePart),
    escapeCsvField(dri?.name ?? ''),
    escapeCsvField(profileUrl),
    escapeCsvField(outcomeId),
    escapeCsvField(getOutcomeKindFromLog(log)),
    metricName,
    escapeCsvField(log.reason ?? ''),
  ];
  const row = (change: string, before: string, after: string) => [...prefix, change, before, after];

  if (!log.before && log.after) {
    return [row('(added)', '', escapeCsvField(getMetricTitlePlainText(log.after)))];
  }
  if (log.before && !log.after) {
    return [row('(deleted)', escapeCsvField(getMetricTitlePlainText(log.before)), '')];
  }
  if (!log.before || !log.after) return [];

  try {
    const beforeObj = JSON.parse(log.before) as Record<string, unknown>;
    const afterObj = JSON.parse(log.after) as Record<string, unknown>;
    const keys = new Set([...Object.keys(beforeObj), ...Object.keys(afterObj)]);
    const out: string[][] = [];
    for (const key of keys) {
      /* Never emit CSV rows for order/current even when log has other changes. */
      if (key === 'order' || key === 'current') continue;
      const b = beforeObj[key];
      const a = afterObj[key];
      if (JSON.stringify(b) === JSON.stringify(a)) continue;
      if (
        key === 'complete_date' &&
        afterObj.complete === true &&
        JSON.stringify(beforeObj.complete) !== JSON.stringify(afterObj.complete)
      ) {
        continue;
      }
      const label = METRIC_FIELD_LABELS[key] ?? key;
      out.push(
        row(
          label,
          escapeCsvField(formatMetricFieldValue(key, b)),
          escapeCsvField(formatMetricFieldValue(key, a))
        )
      );
    }
    return out;
  } catch {
    return [row('(edit)', escapeCsvField(log.before), escapeCsvField(log.after))];
  }
}

function buildAuditLogCsv(
  logs: WorkstreamAuditLogType[],
  workstreamsById: Map<number, Workstream>
): string {
  const header = [
    'workstream id',
    'workstream name',
    'date',
    'time',
    'dri name',
    'profile',
    'milestone id',
    'milestone type',
    'metric name',
    'reason',
    'change',
    'before',
    'after',
  ];
  const rows: string[] = [];
  for (const log of logs) {
    if (log.workstream_id == null || isFilteredMetricChange(log)) continue;
    const ws = workstreamsById.get(log.workstream_id);
    const dri = ws ? getWorkstreamDri(ws) : null;
    const driInfo = dri == null ? null : {name: dri?.name ?? '', ldap: dri?.ldap ?? ''};
    for (const cells of expandLogToCsvRows(log, ws, driInfo)) {
      rows.push(cells.join(','));
    }
  }
  return [header.join(','), ...rows].join('\r\n');
}

function downloadCsv(
  logs: WorkstreamAuditLogType[],
  workstreamsById: Map<number, Workstream>,
  startDate: string,
  endDate: string
) {
  const csv = buildAuditLogCsv(logs, workstreamsById);
  const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'});
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `workstream-metric-audit-${startDate}-to-${endDate}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const defaultDateRange = () => {
  const now = new Date();
  const lastWeek = subWeeks(now, 1);
  const start = startOfWeek(lastWeek, {weekStartsOn: 1});
  const end = endOfWeek(lastWeek, {weekStartsOn: 1});
  return {start, end};
};

const isDeletion = (log: WorkstreamAuditLogType) => !!log.before && !log.after;
const isAddition = (log: WorkstreamAuditLogType) => !!log.after && !log.before;

/** Logs whose only changes are in these keys are omitted entirely (CSV + UI table). */
const FILTERED_METRIC_KEYS = new Set(['current', 'complete', 'order']);

function isIgnoredMetricChange(log: WorkstreamAuditLogType, ignoredKeys: Set<string>): boolean {
  if (log.type !== 'metric' || !log.before || !log.after) return false;
  try {
    const before = JSON.parse(log.before) as Record<string, unknown>;
    const after = JSON.parse(log.after) as Record<string, unknown>;
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    for (const key of keys) {
      if (ignoredKeys.has(key)) continue;
      const b = before[key];
      const a = after[key];
      if (JSON.stringify(b) !== JSON.stringify(a)) return false;
    }
    return true;
  } catch {
    return false;
  }
}

function isFilteredMetricChange(log: WorkstreamAuditLogType): boolean {
  return isIgnoredMetricChange(log, FILTERED_METRIC_KEYS);
}

function aggregateByWorkstream(logs: WorkstreamAuditLogType[]) {
  const byId = new Map<
    number,
    {adds: number; deletes: number; changes: number; logs: WorkstreamAuditLogType[]}
  >();
  for (const log of logs) {
    if (isFilteredMetricChange(log)) continue;
    const id = log.workstream_id ?? 0;
    if (!id) continue;
    let row = byId.get(id);
    if (!row) {
      row = {adds: 0, deletes: 0, changes: 0, logs: []};
      byId.set(id, row);
    }
    row.logs.push(log);
    if (isAddition(log)) row.adds += 1;
    else if (isDeletion(log)) row.deletes += 1;
    else if (log.before && log.after) row.changes += 1;
  }
  return byId;
}

type AuditRow = {
  workstreamId: number;
  title: string | null;
  dri: EmployeeRoleLevel | null;
  metrics: Workstream['metrics'];
  changes: number;
  adds: number;
  deletes: number;
  logs: WorkstreamAuditLogType[];
};

const AUDIT_COLUMNS: Array<{
  type: string;
  minWidth: number;
  sort?: (a: AuditRow, b: AuditRow) => number;
}> = [
  {
    type: 'workstream',
    minWidth: 280,
    sort: (a, b) => (a.title ?? '').localeCompare(b.title ?? ''),
  },
  {
    type: 'dri',
    minWidth: 160,
    sort: (a, b) => (a.dri?.name ?? '').localeCompare(b.dri?.name ?? ''),
  },
  {type: 'changes', minWidth: 80, sort: (a, b) => a.changes - b.changes},
  {type: 'adds', minWidth: 80, sort: (a, b) => a.adds - b.adds},
  {type: 'deletes', minWidth: 80, sort: (a, b) => a.deletes - b.deletes},
];

const MetricOnlyLogView = ({
  logs,
  metrics = [],
}: {
  logs: WorkstreamAuditLogType[];
  metrics?: Workstream['metrics'];
}) => {
  const sorted = useMemo(
    () =>
      [...logs].sort((a, b) => parseISO(`${b.date}Z`).getTime() - parseISO(`${a.date}Z`).getTime()),
    [logs]
  );
  const grouped = groupByTimeAndType(sorted);

  return (
    <Stack gap="16">
      {Object.entries(grouped).map(([timestamp, changesByType]) => {
        const changeTypes = Object.keys(changesByType);
        const firstChange = changesByType[changeTypes[0]]?.[0];
        if (!firstChange) return null;
        const cardTimestamp =
          Object.values(changesByType)
            .flat()
            .reduce<string | null>((latest, change) => {
              if (!latest || Date.parse(change.date) > Date.parse(latest)) {
                return change.date;
              }
              return latest;
            }, null) ?? firstChange.date;

        const reasonChanges: typeof changesByType = {};
        if (changesByType['metric_added'] || changesByType['metric_deleted']) {
          reasonChanges['metric_added_deleted'] = [
            ...(changesByType['metric_added'] || []),
            ...(changesByType['metric_deleted'] || []),
          ].sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
        }

        return (
          <Box key={timestamp}>
            <UpdatePost
              employee={firstChange.employee}
              update={{created_at: cardTimestamp}}
              featured
              includeArchived
            >
              <Stack gap="8">
                {Object.entries(reasonChanges).map(([type, changes]) => (
                  <Stack key={type} gap={type === 'metric_added_deleted' ? '2' : '8'}>
                    <Text isBold>{t(`workstream_audit_${type}`)}</Text>
                    {changes.map(({before, after, reason}) => (
                      <Stack key={`${before ?? ''}-${after ?? ''}`} gap="8">
                        <Stack gap="2">
                          {before && (
                            <ChangeLogLine
                              change="remove"
                              type={type}
                              value={getMetricTitle(before)}
                              node={
                                <RichTextArea value={JSON.parse(before).title} textColor="subtle" />
                              }
                              useFullWidth
                            />
                          )}
                          {after && (
                            <ChangeLogLine
                              change="add"
                              type={type}
                              value={getMetricTitle(after)}
                              useFullWidth
                            />
                          )}
                        </Stack>
                        {reason && (
                          <Box marginTop="16" marginBottom="16">
                            <AuditLogReason reason={reason} border="top" />
                          </Box>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                ))}
                {(changesByType['metric'] || changesByType['metric_forecast']) && (
                  <WorkstreamChangesByMetric
                    metricChanges={changesByType['metric'] || []}
                    metricForecastChanges={changesByType['metric_forecast'] || []}
                    metrics={metrics}
                  />
                )}
                {Object.entries(changesByType).map(([type, changes]) =>
                  ['metric_added', 'metric_deleted', 'metric', 'metric_forecast'].includes(
                    type
                  ) ? null : (
                    <Stack key={`${timestamp}-${type}`} gap="8">
                      <Text isBold>{t(`workstream_audit_${type}`)}</Text>
                      {changes.map(({before, after, reason}) => (
                        <Stack key={`${before ?? ''}-${after ?? ''}`} gap="8">
                          <Stack gap="2">
                            {before && (
                              <ChangeLogLine
                                change="remove"
                                type={type}
                                value={before}
                                metrics={metrics}
                              />
                            )}
                            {after && (
                              <ChangeLogLine
                                change="add"
                                type={type}
                                value={after}
                                metrics={metrics}
                              />
                            )}
                          </Stack>
                          {reason && (
                            <Box marginTop="16" marginBottom="16">
                              <AuditLogReason reason={reason} border="top" />
                            </Box>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  )
                )}
              </Stack>
            </UpdatePost>
          </Box>
        );
      })}
    </Stack>
  );
};

const WorkstreamAuditRow = ({
  workstreamId,
  title,
  dri,
  metrics,
  changes,
  adds,
  deletes,
  logs,
  expanded,
  onToggle,
}: {
  workstreamId: number;
  title: string;
  dri: EmployeeRoleLevel | null;
  metrics: Workstream['metrics'];
  changes: number;
  adds: number;
  deletes: number;
  logs: WorkstreamAuditLogType[];
  expanded: boolean;
  onToggle: () => void;
}) => (
  <>
    <Table.Row isSelectable onClick={onToggle}>
      <Table.Cell>
        <Box display="flex" alignItems="flex-start" style={{gap: 4}}>
          <UIIcon
            style={{flexShrink: 0, marginTop: 3}}
            src={expanded ? ChevronDownLine : ChevronRightLine}
            size="small"
            className={atoms({color: 'Text Subtle'})}
          />
          <Text>{title || `Workstream ${workstreamId}`}</Text>
        </Box>
      </Table.Cell>
      <Table.Cell>
        <Text size="small">{dri ? <EmployeeButton employee={dri} /> : '—'}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text size="small">{changes}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text size="small">{adds}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text size="small">{deletes}</Text>
      </Table.Cell>
    </Table.Row>
    {expanded && (
      <Table.Row>
        <Table.Cell colSpan={AUDIT_COLUMNS.length}>
          <Box paddingY="16" paddingLeft="24" backgroundColor="Background Subtle">
            <MetricOnlyLogView logs={logs} metrics={metrics} />
          </Box>
        </Table.Cell>
      </Table.Row>
    )}
  </>
);

const fetchMetricLogs = async (
  startDate: string,
  endDate: string
): Promise<WorkstreamAuditLogType[]> =>
  __request(OpenAPI, {
    method: 'GET',
    url: '/api/v1/workstream/logs/metrics',
    query: {start_date: startDate, end_date: endDate},
    errors: {422: 'Validation Error'},
  }) as Promise<WorkstreamAuditLogType[]>;

export const AdminWorkstreamAuditLog = () => {
  const navigate = useNavigate();
  const pulseUser = useAtomValue(pulseUserAtom);
  const defaultRange = useMemo(() => defaultDateRange(), []);
  const [selectedRange, setSelectedRange] = useState<DatePickerRangeSelection>([
    defaultRange.start,
    defaultRange.end,
  ]);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const handleSelection = ({
    startDate,
    endDate,
  }: {
    startDate: DatePickerSelection;
    endDate: DatePickerSelection;
  }) => {
    setSelectedRange([startDate, endDate]);
  };
  const handleChangeStart = (date: DatePickerSelection) => {
    setSelectedRange([date ?? selectedRange[0], selectedRange[1]]);
  };
  const handleChangeEnd = (date: DatePickerSelection) => {
    setSelectedRange([selectedRange[0], date ?? selectedRange[1]]);
  };

  const startDateStr = selectedRange[0] != null ? formatDateForApi(selectedRange[0]) : '';
  const endDateStr = selectedRange[1] != null ? formatDateForApi(selectedRange[1]) : '';
  const hasValidRange = selectedRange[0] != null && selectedRange[1] != null;
  const [apiStart, apiEnd] = hasValidRange
    ? selectedRange[0]! <= selectedRange[1]!
      ? [startDateStr, endDateStr]
      : [endDateStr, startDateStr]
    : ['', ''];

  useDocumentTitle('Workstream metrics audit');

  const {data: logs = [], isLoading: logsLoading} = useQuery({
    queryKey: ['admin', 'workstream-metric-logs', apiStart, apiEnd],
    queryFn: () => fetchMetricLogs(apiStart, apiEnd),
    enabled: hasValidRange && !!apiStart && !!apiEnd,
  });

  const byWorkstream = useMemo(() => aggregateByWorkstream(logs), [logs]);
  const workstreamIds = useMemo(() => Array.from(byWorkstream.keys()), [byWorkstream]);

  const workstreamQueries = useQueries({
    queries: workstreamIds.map((id) => ({
      queryKey: ['workstream', id],
      queryFn: async (): Promise<Workstream> => {
        const response = await getService(WorkstreamService).getApiV1WorkstreamIdGet(id);
        return {...response, metrics: getFullWorkstreamMetrics(response.metrics)};
      },
      enabled: workstreamIds.length > 0,
    })),
  });

  const workstreamDataList = workstreamIds.map((_, i) => workstreamQueries[i]?.data);
  const workstreamsById = useMemo(() => {
    const map = new Map<number, Workstream>();
    workstreamIds.forEach((id, i) => {
      const data = workstreamDataList[i];
      if (id && data) map.set(id, data);
    });
    return map;
  }, [workstreamIds, workstreamDataList]);

  const rows = useMemo(() => {
    return workstreamIds.map((id) => {
      const row = byWorkstream.get(id)!;
      const ws = workstreamsById.get(id);
      const dri = ws ? getWorkstreamDri(ws) : null;
      return {
        workstreamId: id,
        title: ws?.name ?? null,
        dri: dri ?? null,
        metrics: ws?.metrics ?? [],
        changes: row.changes,
        adds: row.adds,
        deletes: row.deletes,
        logs: row.logs,
      };
    });
  }, [workstreamIds, byWorkstream, workstreamsById]);

  const isLoadingWorkstreams = workstreamQueries.some((q) => q.isLoading);

  if (!pulseUser) {
    return null;
  }

  if (!isUserAdmin(pulseUser)) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Workstream metrics audit', to: ROUTE_PATHS.ADMIN_WORKSTREAM_AUDIT_LOG},
      ]}
    >
      <Stack gap="24">
        <Title size={24}>Workstream metrics audit</Title>

        <Box display="flex" alignItems="center" justifyContent="space-between" style={{gap: 16}}>
          <DatePickerRange selectedRange={selectedRange} onSelection={handleSelection}>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              style={{width: 300, gap: 8}}
            >
              <DatePickerRange.StartInput
                size="small"
                id="admin-audit-log-start_date"
                value={selectedRange[0]}
                onChange={handleChangeStart}
                autocomplete="off"
              />

              <UIIcon src={ArrowRightLine} />

              <DatePickerRange.EndInput
                size="small"
                id="admin-audit-log-end_date"
                value={selectedRange[1]}
                onChange={handleChangeEnd}
                autocomplete="off"
              />
            </Box>
          </DatePickerRange>
          {!logsLoading && !isLoadingWorkstreams && logs.length > 0 && hasValidRange && (
            <Button
              variant="outline"
              withIconStart={<UIIcon src={DownloadLine} />}
              onClick={() => downloadCsv(logs, workstreamsById, apiStart, apiEnd)}
            >
              {t('admin_audit_log_download_csv')}
            </Button>
          )}
        </Box>

        {logsLoading || isLoadingWorkstreams ? (
          <Box display="flex" justifyContent="center" style={{paddingTop: 48, paddingBottom: 48}}>
            <Spinner monochromatic />
          </Box>
        ) : (
          <Table<AuditRow>
            columns={AUDIT_COLUMNS}
            data={rows}
            columnLabelOverrides={{
              workstream: 'Title',
              dri: 'DRI',
              changes: '# changes',
              adds: '# adds',
              deletes: '# deletes',
            }}
          >
            <Table.Header />
            <Table.Body>
              {rows.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={AUDIT_COLUMNS.length}>
                    <Text color="faint">{t('admin_audit_log_empty')}</Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                <Table.Rows
                  renderRow={(row) => (
                    <WorkstreamAuditRow
                      key={row.workstreamId}
                      workstreamId={row.workstreamId}
                      title={row.title ?? ''}
                      dri={row.dri}
                      metrics={row.metrics}
                      changes={row.changes}
                      adds={row.adds}
                      deletes={row.deletes}
                      logs={row.logs}
                      expanded={expandedId === row.workstreamId}
                      onToggle={() =>
                        setExpandedId((id) => (id === row.workstreamId ? null : row.workstreamId))
                      }
                    />
                  )}
                />
              )}
            </Table.Body>
          </Table>
        )}
      </Stack>
    </Layout.Container>
  );
};
